import { ref } from 'vue'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { getToken } from '@/utils/auth'

function buildCustomerServiceHubUrl() {
  const token = getToken()
  let signalRUrl = import.meta.env.MODE === 'development'
    ? 'http://localhost:5276/api/hubs/customerService'
    : '/api/hubs/customerService'

  if (token) {
    const separator = signalRUrl.includes('?') ? '&' : '?'
    signalRUrl += `${separator}access_token=${token}`
  }

  return signalRUrl
}

export function useCustomerServiceHub(options = {}) {
  const connectionState = ref('Disconnected')
  const connection = ref(null)
  const joinedQueueGroup = ref(false)
  const activeConversationId = ref(0)

  async function connect() {
    if (connection.value) {
      return connection.value
    }

    const instance = new HubConnectionBuilder()
      .withUrl(buildCustomerServiceHubUrl())
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build()

    instance.on('ManualServiceQueueChanged', payload => {
      options.onManualQueueChanged?.(payload)
    })

    instance.on('CustomerServiceConversationChanged', payload => {
      options.onConversationChanged?.(payload)
    })

    instance.onreconnecting(() => {
      connectionState.value = 'Reconnecting'
    })

    instance.onreconnected(async () => {
      connectionState.value = 'Connected'
      if (joinedQueueGroup.value) {
        await joinManualQueueGroup()
      }
      if (activeConversationId.value > 0) {
        await watchConversation(activeConversationId.value)
      }
    })

    instance.onclose(() => {
      connectionState.value = 'Disconnected'
    })

    await instance.start()
    connection.value = instance
    connectionState.value = 'Connected'

    return instance
  }

  async function disconnect() {
    if (!connection.value) {
      return
    }

    try {
      if (joinedQueueGroup.value) {
        await connection.value.invoke('LeaveManualServiceQueueGroup')
      }

      if (activeConversationId.value > 0) {
        await connection.value.invoke('LeaveConversationGroup', activeConversationId.value)
      }
    } catch (error) {
      console.error('关闭客服 SignalR 分组失败', error)
    }

    try {
      await connection.value.stop()
    } catch (error) {
      console.error('关闭客服 SignalR 连接失败', error)
    }

    connection.value = null
    connectionState.value = 'Disconnected'
    joinedQueueGroup.value = false
    activeConversationId.value = 0
  }

  async function joinManualQueueGroup() {
    const instance = await connect()
    await instance.invoke('JoinManualServiceQueueGroup')
    joinedQueueGroup.value = true
  }

  async function watchConversation(conversationRecordId) {
    const nextConversationId = Number(conversationRecordId || 0)
    const instance = await connect()

    if (activeConversationId.value > 0 && activeConversationId.value !== nextConversationId) {
      await instance.invoke('LeaveConversationGroup', activeConversationId.value)
    }

    activeConversationId.value = nextConversationId
    if (nextConversationId > 0) {
      await instance.invoke('JoinConversationGroup', nextConversationId)
    }
  }

  return {
    connectionState,
    connect,
    disconnect,
    joinManualQueueGroup,
    watchConversation,
  }
}
